import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaCheck, FaTimes } from "react-icons/fa";
import styles from "./Viewassessment.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";

const OPTION_LETTERS = ["a", "b", "c", "d", "e", "f"];

function ViewStudentAssessmentResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";

  const persistedNav = (() => {
    try {
      const raw = sessionStorage.getItem("assessment_result_nav");
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  })();

  const assessmentId =
    location?.state?.assessment_id ??
    location?.state?.ID ??
    persistedNav?.assessment_id ??
    persistedNav?.ID ??
    "";
  const studentId =
    location?.state?.student_id ?? persistedNav?.student_id ?? user_id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (assessmentId && studentId) {
      fetchAssessmentResult();
    } else {
      setError("Missing assessment or student information");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId, studentId]);

  const fetchAssessmentResult = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        school_id: school_id,
        assessment_id: assessmentId,
        student_id: studentId,
      };

      const response = await AxiosInstance.post(
        `/book/assessment/student/result`,
        payload,
      );

      if (response?.data?.status) {
        setData(response.data.data);
      } else {
        setError(response?.data?.message || "Failed to fetch assessment");
      }
    } catch (err) {
      console.error("Error fetching assessment result:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/user/assessment");
  };

  const getOptionText = (question, letter) => {
    if (!letter) return "";
    const key = `option_${letter.toLowerCase()}`;
    return question[key];
  };

  if (loading) {
    return (
      <div className={styles.loaderWrapper}>
        <div className={styles.spinner}></div>
        <p>{translations?.labels?.loading || "Loading assessment..."}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorWrapper}>
        <p>⚠ {error}</p>
        <button onClick={handleBack} className={styles.backButton}>
          {translations?.labels?.back || "Back"}
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { assessment, result, questions } = data;
  const percentage = parseFloat(result?.percentage || 0);
  const isPassed = (result?.status || "").toString().toLowerCase() === "pass";

  return (
    <div className={styles.container}>
      {/* Summary Bar */}
      <div className={styles.summaryBar}>
        <div className={styles.summaryItem}>
          <div className={styles.scoreValue}>{percentage.toFixed(0)}%</div>
          <div className={styles.scoreLabel}>
            {translations?.labels?.totalScore || "Total Score"}
          </div>
        </div>

        <div className={styles.divider}></div>

        <div className={styles.summaryItem}>
          <div
            className={`${styles.statusValue} ${
              isPassed ? styles.passed : styles.failed
            }`}
          >
            {isPassed
              ? translations?.labels?.pass || "Pass"
              : translations?.labels?.fail || "Fail"}
          </div>
          <div className={styles.scoreLabel}>
            {translations?.labels?.status || "Status"}
          </div>
        </div>

        <div className={styles.divider}></div>

        <div className={`${styles.statBox} ${styles.correctBox}`}>
          <div className={styles.statValue}>
            <span className={styles.checkIcon}>
              <FaCheck />
            </span>
            {result?.correct_answers ?? 0}
          </div>
          <div className={styles.statLabel}>
            {translations?.labels?.correctAnswers || "Correct Answers"}
          </div>
        </div>

        <div className={`${styles.statBox} ${styles.incorrectBox}`}>
          <div className={styles.statValue}>
            <span className={styles.crossIcon}>
              <FaTimes />
            </span>
            {result?.wrong_answers ?? 0}
          </div>
          <div className={styles.statLabel}>
            {translations?.labels?.incorrectAnswers || "Unscored Answers"}
          </div>
        </div>
      </div>

      {/* Topic Information */}
      <div className={styles.topicInfo}>
        <div className={styles.topicLeft}>
          <p>
            <strong>{translations?.labels?.topic || "Topic"} :</strong>{" "}
            {assessment?.topic || "-"}
          </p>
          <p>
            <strong>{translations?.labels?.subTopic || "Sub Topic"} :</strong>{" "}
            {assessment?.sub_topic || "-"}
          </p>
        </div>
        <div className={styles.topicRight}>
          <p>
            <strong>{translations?.labels?.subject || "Subject"} :</strong>{" "}
            {assessment?.subject || "-"}
          </p>
          <p>
            <strong>{translations?.labels?.class || "Class"} :</strong>{" "}
            {assessment?.class_name || "-"}
            {assessment?.section_id ? ` - ${assessment.section_id}` : ""}
          </p>
        </div>
      </div>

      {/* Questions List */}
      <div className={styles.questionsList}>
        {questions?.map((question, index) => {
          const studentAnswer = question.student_answer;
          const correctAnswer = question.correct_answer;
          const isCorrect = question.is_correct === 1;

          return (
            <div key={question.id} className={styles.questionCard}>
              <p className={styles.questionText}>
                {index + 1}. {question.assessment_question}
              </p>

              <div className={styles.optionsGrid}>
                {OPTION_LETTERS.map((letter) => {
                  const optionText = getOptionText(question, letter);
                  if (!optionText) return null;

                  const isStudentChoice = studentAnswer === letter;
                  const isCorrectChoice = correctAnswer === letter;

                  let optionClass = styles.option;
                  if (isStudentChoice && isCorrect) {
                    optionClass += ` ${styles.correctSelected}`;
                  } else if (isStudentChoice && !isCorrect) {
                    optionClass += ` ${styles.wrongSelected}`;
                  }

                  return (
                    <div key={letter} className={optionClass}>
                      <span
                        className={`${styles.radio} ${
                          isStudentChoice ? styles.radioSelected : ""
                        } ${
                          isStudentChoice && isCorrect ? styles.radioCorrect : ""
                        } ${
                          isStudentChoice && !isCorrect ? styles.radioWrong : ""
                        }`}
                      ></span>
                      <span className={styles.optionLabel}>
                        {letter}) {optionText}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div
                className={`${styles.explanationBanner} ${styles.correctBanner}`}
              >
                <span className={styles.bannerIcon}>
                <FaCheck /> 
                </span>
                <div className={styles.bannerContent}>
                  <p className={styles.bannerAnswer}>
                    {correctAnswer} {getOptionText(question, correctAnswer)}
                  </p>
                  <p className={styles.bannerExplanation}>
                    <strong>
                      {translations?.labels?.explanation || "Explanation"} :{" "}
                    </strong>
                    {question.explanation}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <button
          onClick={handleBack}
          className={styles.cancelButton}
          type="button"
        >
          {translations?.labels?.cancel || "Cancel"}
        </button>
      </div>
    </div>
  );
}

export default ViewStudentAssessmentResult;